import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiMail, FiClock, FiChevronDown, FiSend } from 'react-icons/fi';

const Support = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "How do I add a playlist to TVVerse?",
      a: "Open TVVerse, click the '+' or 'Add Playlist' button on the main screen. You can either paste a URL pointing to an M3U file, or browse your local files to select an M3U file downloaded to your computer."
    },
    {
      q: "Can I use PressVerse without an internet connection?",
      a: "Writing, editing, formatting and exporting all work offline. You only need an internet connection for the parts that call out: AI generation with your provider (OpenAI, Anthropic, Gemini, DeepSeek, Mistral), niche research, and publishing to Amazon KDP."
    },
    {
      q: "Where is my data saved?",
      a: "All your settings, favorites, and manuscripts are saved locally on your Windows device, typically in the %APPDATA% folder specific to the app. We do not store any of your data on our servers."
    },
    {
      q: "Do I need an account?",
      a: "No. None of our apps require you to sign up, create an account, or log in. You purchase/download the app and it works immediately."
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const subject = `Support Request: ${formData.get('app')}`;
    const body = `Name: ${formData.get('name')}%0D%0AEmail: ${formData.get('email')}%0D%0A%0D%0ADescription:%0D%0A${formData.get('description')}`;
    window.location.href = `mailto:hello@mediaverselabs.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Support Center</h1>
          <p className="text-xl text-muted max-w-2xl mx-auto">
            We're here to help. Check our FAQ or drop us a message.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form Side */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-card border border-border rounded-none p-8 shadow-xl"
          >
            <div className="flex items-center gap-4 mb-8 pb-8 border-b border-border">
              <div className="flex items-center gap-2 text-muted text-sm sm:text-base">
                <SafeIcon icon={FiMail} />
                <span>hello@mediaverselabs.com</span>
              </div>
              <div className="flex items-center gap-2 text-muted text-sm sm:text-base">
                <SafeIcon icon={FiClock} />
                <span>24-48h response</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">Name</label>
                  <input 
                    required 
                    name="name"
                    type="text" 
                    className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-accent-red focus:ring-1 focus:ring-accent-red transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">Email</label>
                  <input 
                    required 
                    name="email"
                    type="email" 
                    className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-accent-red focus:ring-1 focus:ring-accent-red transition-all"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-white">Affected App</label>
                <select 
                  name="app"
                  className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-accent-red focus:ring-1 focus:ring-accent-red transition-all appearance-none"
                >
                  <option value="PressVerse">PressVerse</option>
                  <option value="TVVerse">TVVerse</option>
                  <option value="RadioVerse">RadioVerse</option>
                  <option value="ClassicVerse">ClassicVerse</option>
                  <option value="General">General Inquiry</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-white">Description</label>
                <textarea 
                  required 
                  name="description"
                  rows="5" 
                  className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-accent-red focus:ring-1 focus:ring-accent-red transition-all resize-none"
                  placeholder="How can we help you?"
                ></textarea>
              </div>

              <button type="submit" className="w-full btn-primary bg-white text-background hover:bg-gray-200 mt-4">
                <SafeIcon icon={FiSend} className="w-4 h-4" /> Send Message
              </button>
            </form>
          </motion.div>

          {/* FAQ Side */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2 className="text-2xl font-bold text-white mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="border border-border rounded-md bg-card/50 overflow-hidden transition-colors hover:border-[#3a4059]"
                >
                  <button 
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none"
                  >
                    <span className="font-medium text-white">{faq.q}</span>
                    <SafeIcon 
                      icon={FiChevronDown} 
                      className={`w-5 h-5 text-muted transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === index ? 'max-h-40 pb-4 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <p className="text-muted leading-relaxed text-sm">
                      {faq.a}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Support;